import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { VenueTeaser } from "Venues/components";
import { dispatchGetVenueAction, venueSelectorCreator } from "Venues/state";
import { ICardTheme } from "shared/types";
import { StageCapacityForm } from "Bands/components";
import { CreateStageModal } from "Bands/containers";
import { CreateStageCapacityModal } from "../CreateStageCapacityModal/CreateStageCapacityModal";
import { useVenueBlockContext } from "Bands/contexts";

interface SelectedVenueTeaserProps {
  canCreateStageOrCapacity?: boolean;
}

export const SelectedVenueTeaser: React.FC<SelectedVenueTeaserProps> = ({
  canCreateStageOrCapacity,
}) => {
  const { venueId, stageId, setStageId, capacityId, setCapacityId } =
    useVenueBlockContext();

  const venueIdSelector = useMemo(
    () => venueSelectorCreator(venueId || ""),
    [venueId]
  );
  const venue = useSelector(venueIdSelector);

  const dispatch = useDispatch();
  useEffect(() => {
    if (!venueId) return;

    dispatchGetVenueAction(dispatch, venueId);
  }, [dispatch, venueId]);

  // selected stage
  const selectedStage = useMemo(
    () => venue?.stages?.find(({ id }) => id === stageId),
    [stageId, venue?.stages]
  );

  // add stage/capacity modals
  const [showCreateStageModal, setShowCreateStageModal] = useState(false);
  const [showCreateCapacityModal, setShowCreateCapacityModal] = useState(false);

  const defaultValues = useMemo(
    () => ({ stage: stageId, capacity: capacityId }),
    [stageId, capacityId]
  );

  // reset capacity ID if no stage selected
  useEffect(() => {
    if (!stageId && capacityId) {
      setCapacityId(null);
    }
  }, [capacityId, setCapacityId, stageId]);

  const handleAddStage = useMemo(
    () =>
      canCreateStageOrCapacity
        ? () => setShowCreateStageModal(true)
        : undefined,
    [canCreateStageOrCapacity]
  );

  const handleAddCapacity = useMemo(
    () =>
      canCreateStageOrCapacity
        ? () => setShowCreateCapacityModal(true)
        : undefined,
    [canCreateStageOrCapacity]
  );

  if (!venueId || !venue) return null;

  return (
    <>
      <VenueTeaser {...venue} theme={ICardTheme.secondary} showMoreInfo={false}>
        <StageCapacityForm
          stages={venue.stages}
          capacities={selectedStage?.stageCapacities}
          defaultValues={defaultValues}
          handleAddStage={handleAddStage}
          handleAddCapacity={handleAddCapacity}
          handleChangeStage={(stageId) => setStageId(stageId)}
          handleChangeCapacity={(capacityId) => setCapacityId(capacityId)}
          showCapacityField={!!selectedStage}
        />
      </VenueTeaser>
      {showCreateStageModal && (
        <CreateStageModal
          venueId={venueId}
          handleClose={() => setShowCreateStageModal(false)}
          onCreateSuccess={(stageId) => setStageId(stageId)}
        />
      )}
      {showCreateCapacityModal && stageId && (
        <CreateStageCapacityModal
          venueId={venueId}
          stageId={stageId}
          handleClose={() => setShowCreateCapacityModal(false)}
          onCreateSuccess={(capacityId) => setCapacityId(capacityId)}
        />
      )}
    </>
  );
};
